import * as React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import type { User, AuthError } from 'firebase/auth';
import { auth } from '../firebase';

interface AuthContextType {
  currentUser: User | null;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      // Get the ID token to check for custom claims
      const tokenResult = await userCredential.user.getIdTokenResult();
      const adminClaim = tokenResult.claims.admin === true;
      setIsAdmin(adminClaim);
      
      // Store token in localStorage for API requests
      const token = await userCredential.user.getIdToken();
      localStorage.setItem('firebaseToken', token);
      
    } catch (error) {
      const authError = error as AuthError;
      throw new Error(authError.message);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await signOut(auth);
      setIsAdmin(false);
      localStorage.removeItem('firebaseToken');
    } catch (error) {
      const authError = error as AuthError;
      throw new Error(authError.message);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        try {
          // Get the ID token result to check admin claim
          const tokenResult = await user.getIdTokenResult();
          const adminClaim = tokenResult.claims.admin === true;
          setIsAdmin(adminClaim);
          
          // Store token in localStorage for API requests
          const token = await user.getIdToken();
          localStorage.setItem('firebaseToken', token);
        } catch (error) {
          console.error('Error getting user claims:', error);
          setIsAdmin(false);
          localStorage.removeItem('firebaseToken');
        }
      } else {
        setIsAdmin(false);
        localStorage.removeItem('firebaseToken');
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    isAdmin,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 